import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalFooter,
} from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { AccordionSelect } from "@/components/ui/Select/AccordionSelect";
import { useMemo, useState } from "react";
import { useRawMaterials } from "@/hooks/useRawMaterials";

const typeOptions = [
  { value: "solid", label: "Solid" },
  { value: "liquid", label: "Liquid" },
];

const processOptions = ["A", "B", "C", "D", "E"].map((value) => ({
  value,
  label: value,
}));

export default function AddStandardIngredientModal({ isOpen, onClose, onConfirm, isConfectionary = false }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    type: "",
    process: "",
    ingredient: "",
    bffRate: "",
    clientRate: "",
    quantity: "",
  });

  const { data: rawMaterialsData } = useRawMaterials({
    searchTerm,
    status: "active",
    type: formData.type || "all",
    page: 1,
    limit: 100,
  });

  const ingredientOptions = useMemo(() => {
    const items = rawMaterialsData?.data || [];
    return items.map((item) => ({
      value: item.id || item._id,
      label: item.name,
    }));
  }, [rawMaterialsData]);

  const handleChange = (field, value) => {
    setFormData(prev => {
      if (field === "type") {
        return { ...prev, type: value, ingredient: "", bffRate: "", clientRate: "" };
      }
      if (field === "ingredient") {
        const selectedIngredient = (rawMaterialsData?.data || []).find(
          (item) => (item.id || item._id) === value
        );
        const parsedBffRate = Number(String(selectedIngredient?.rawBffRate ?? selectedIngredient?.bffRate ?? selectedIngredient?.cost ?? "0").replace(/,/g, ""));
        const bffRateStr = Number.isFinite(parsedBffRate) && parsedBffRate > 0 ? String(parsedBffRate) : "";
        const parsedClientRate = Number(String(selectedIngredient?.rawClientRate ?? selectedIngredient?.clientRate ?? selectedIngredient?.cost ?? "0").replace(/,/g, ""));
        const clientRateStr = Number.isFinite(parsedClientRate) && parsedClientRate > 0 ? String(parsedClientRate) : "";
        const rawType = selectedIngredient?.type ? String(selectedIngredient.type).toLowerCase() : "";

        return {
          ...prev,
          ingredient: value,
          type: prev.type || rawType,
          bffRate: bffRateStr || prev.bffRate,
          clientRate: clientRateStr || prev.clientRate,
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleConfirm = () => {
    if (formData.ingredient) {
      const selectedIngredient = (rawMaterialsData?.data || []).find(
        (item) => (item.id || item._id) === formData.ingredient
      );
      const parsedBffRate = Number(String(selectedIngredient?.rawBffRate ?? selectedIngredient?.bffRate ?? selectedIngredient?.cost ?? "0").replace(/,/g, ""));
      const defaultBff = Number.isFinite(parsedBffRate) ? parsedBffRate : 0;
      const parsedClientRate = Number(String(selectedIngredient?.rawClientRate ?? selectedIngredient?.clientRate ?? selectedIngredient?.cost ?? "0").replace(/,/g, ""));
      const defaultClient = Number.isFinite(parsedClientRate) ? parsedClientRate : 0;

      const resolvedType =
        formData.type ||
        (selectedIngredient?.type ? String(selectedIngredient.type).toLowerCase() : "solid");
      const resolvedBffRate =
        formData.bffRate !== "" && !Number.isNaN(Number(formData.bffRate))
          ? Number(formData.bffRate)
          : defaultBff;
      const resolvedClientRate =
        formData.clientRate !== "" && !Number.isNaN(Number(formData.clientRate))
          ? Number(formData.clientRate)
          : (defaultClient || resolvedBffRate);
      const resolvedQuantity =
        formData.quantity !== "" && !Number.isNaN(Number(formData.quantity))
          ? Number(formData.quantity)
          : 0;

      onConfirm?.({
        role: null,
        type: resolvedType,
        process: isConfectionary ? (formData.process || null) : null,
        quantity: resolvedQuantity,
        ingredientSourceType: "rawMaterial",
        sourceId: selectedIngredient?.id || selectedIngredient?._id || formData.ingredient,
        sourceName: selectedIngredient?.name || "",
        sourceCode: null,
        bffRateAtCreation: resolvedBffRate,
        clientRateAtCreation: resolvedClientRate,
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      type: "",
      process: "",
      ingredient: "",
      bffRate: "",
      clientRate: "",
      quantity: "",
    });
    setSearchTerm("");
    onClose();
  };

  const isFormValid = Boolean(formData.ingredient);

  return (
    <Modal open={isOpen} onOpenChange={handleClose}>
      <ModalContent className="max-w-[500px] lg:max-w-[320px] xl:max-w-[355px] 2xl:max-w-[400px] 3xl:max-w-[500px] gap-0 px-5 py-5 rounded-2xl">
        <ModalHeader className="">
          <ModalTitle className="text-lg text-xs xl:text-sm 2xl:text-md 3xl:text-lg font-semibold text-center">
            Add Standard Ingredient
          </ModalTitle>
          <ModalDescription className="sr-only">
            Add a new standard ingredient to the recipe.
          </ModalDescription>
        </ModalHeader>

        <div className="grid gap-2 lg:gap-1 xl:gap-3 2xl:gap-4 3xl:gap-5 py-2">
          {/* Type Dropdown */}
          <div className="space-y-2">
            <label className="text-xs lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-xs font-normal text-lighter-text">Type</label>
            <AccordionSelect
              id="type"
              value={formData.type}
              onChange={(e) => handleChange("type", e.target.value)}
              options={typeOptions}
              placeholder="Select type"
              searchable={false}
              maxHeight="max-h-[90px]"
              className="text-base-color h-8 lg:h-5.5 xl:h-5.5 2xl:h-6.5 3xl:h-8 placeholder:text-xs lg:placeholder:text-[8px] xl:placeholder:text-[10px] 2xl:placeholder:text-xs 3xl:placeholder:text-xs"
            />
          </div>

          {/* Ingredient Dropdown */}
          <div className="space-y-2">
            <label className="text-xs lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-xs font-normal text-lighter-text">Ingredient</label>
            <AccordionSelect
              id="ingredient"
              value={formData.ingredient}
              onChange={(e) => handleChange("ingredient", e.target.value)}
              options={ingredientOptions}
              onSearchChange={setSearchTerm}
              placeholder="Select ingredient"
              searchable={true}
              maxHeight="max-h-[90px]"
              className="text-base-color h-8 lg:h-5.5 xl:h-5.5 2xl:h-6.5 3xl:h-8 placeholder:text-xs lg:placeholder:text-[8px] xl:placeholder:text-[10px] 2xl:placeholder:text-xs 3xl:placeholder:text-xs"
            />
          </div>

          {isConfectionary && (
            <div className="space-y-2">
              <label className="text-xs lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-xs font-normal text-lighter-text">Process</label>
              <AccordionSelect
                id="process"
                value={formData.process}
                onChange={(e) => handleChange("process", e.target.value)}
                options={processOptions}
                placeholder="Select process"
                searchable={false}
                maxHeight="max-h-[90px]"
                className="text-base-color h-8 lg:h-5.5 xl:h-5.5 2xl:h-6.5 3xl:h-8 placeholder:text-xs lg:placeholder:text-[8px] xl:placeholder:text-[10px] 2xl:placeholder:text-xs 3xl:placeholder:text-xs"
              />
            </div>
          )}

          {/* Rates */}
          <div className="grid grid-cols-2 gap-3 lg:gap-2">
            {/* BFF Rate */}
            <div className="space-y-2">
              <label className="text-xs lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-xs font-normal text-lighter-text">BFF Rate</label>
              <Input
                type="number"
                step="0.01"
                value={formData.bffRate}
                onChange={(e) => handleChange("bffRate", e.target.value)}
                placeholder="0.00"
                rightIcon={<span className="flex items-center text-sm lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-sm font-bold text-nav-highlight">৳</span>}
                variant="default"
                inputClassName="text-xs lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-xs placeholder:text-xs lg:placeholder:text-[8px] xl:placeholder:text-[10px] 2xl:placeholder:text-xs 3xl:placeholder:text-xs"
                className="h-8 lg:h-5.5 xl:h-5.5 2xl:h-6.5 3xl:h-8"
              />
            </div>

            {/* Client Rate */}
            <div className="space-y-2">
              <label className="text-xs lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-xs font-normal text-lighter-text">Client Rate</label>
              <Input
                type="number"
                step="0.01"
                value={formData.clientRate}
                onChange={(e) => handleChange("clientRate", e.target.value)}
                placeholder="0.00"
                rightIcon={<span className="flex items-center text-sm lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-sm font-bold text-nav-highlight">৳</span>}
                variant="default"
                inputClassName="text-xs lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-xs placeholder:text-xs lg:placeholder:text-[8px] xl:placeholder:text-[10px] 2xl:placeholder:text-xs 3xl:placeholder:text-xs"
                className="h-8 lg:h-5.5 xl:h-5.5 2xl:h-6.5 3xl:h-8"
              />
            </div>
          </div>

          {/* Quantity Input */}
          <div className="space-y-2">
            <label className="text-xs lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-xs font-normal text-lighter-text">Quantity</label>
            <Input
              type="number"
              step="0.1"
              value={formData.quantity}
              onChange={(e) => handleChange("quantity", e.target.value)}
              placeholder="0.00"
              rightIcon={<span className="flex items-center text-sm lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-sm font-bold text-nav-highlight">g</span>}
              variant="default"
              inputClassName="text-xs lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-xs placeholder:text-xs lg:placeholder:text-[8px] xl:placeholder:text-[10px] 2xl:placeholder:text-xs 3xl:placeholder:text-xs"
              className="h-8 lg:h-5.5 xl:h-5.5 2xl:h-6.5 3xl:h-8"
            />
          </div>
        </div>

        <ModalFooter className="flex-row gap-5 lg:gap-2 xl:gap-3 2xl:gap-3.5 3xl:gap-5 mt-2 sm:justify-between h-9 lg:h-5 xl:h-6.5 2xl:h-7.5 3xl:h-9">
          <Button
            intent="outline"
            onClick={handleClose}
            className="w-full border-table-stroke sm:w-1/2 text-base-color text-xs lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-xs"
          >
            Cancel
          </Button>
          <Button
            intent="primary"
            onClick={handleConfirm}
            disabled={!isFormValid}
            className="w-full text-white sm:w-1/2 bg-primary hover:bg-primary/90 text-xs lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-xs"
          >
            Confirm
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
